/* eslint-disable react-refresh/only-export-components */
/**
 * ComprasQuadroPage (FU-2 — fluxo unificado) — o quadro de Compras sobre o
 * KanbanBoard universal. Card = solicitação (deal-espelho); os itens ficam
 * dentro, cada um com sua situação. A coluna do card deriva do progresso dos
 * itens; arrastar manual = bulk-set da situação dos itens (via o mapeamento da
 * coluna, FU-4). Reusa o board + o motor de automação SEM tocar no engine.
 */
import { createContext, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';

// FASE-C (design system): emoji-ícone banido — Lucide only.
import { Settings2 } from 'lucide-react';

import { KanbanBoard, type KanbanCardProps } from '@/components/kanban/KanbanBoard';
import { Badge, Button, Dialog, DialogHeader, DialogTitle, DialogDescription, DialogContent, PageContainer, PageHeader, Select, SelectOption, useToast } from '@/components/ui';
import { AutomationsPanel } from '@/components/automations';
import { COMPRAS_STAGES, COMPRAS_APROVACAO_SLUGS } from '../workflow-kanban-stages';
import { ClassificarSolicitacaoDialog } from './ClassificarSolicitacaoDialog';
import { podeCotarOCard } from './pode-cotar';
import { useDealActions } from '@/lib/comercial-store';
import { useKanbanColumns } from '@/lib/use-kanban-columns';
import { useAuth } from '@/contexts/AuthContext';
import { usePrefersReducedMotion } from '@/hooks/use-media-query';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import {
  useQuadroCompras,
  useQuadroActions,
  type QuadroCompraCard,
  type ItemSituacao,
  type SolicitacaoCompraUrgencia,
} from '@/lib/solicitacoes-compra-store';
import { useCotacoes, useCotacaoFromSolicitacao, useAddSolicitacaoToCotacao } from '@/lib/compras-store';
import { errMsg } from '@/lib/api-error';
import { cn } from '@/lib/cn';

const SITUACAO_LABEL: Record<ItemSituacao, string> = {
  a_cotar: 'A cotar',
  em_cotacao: 'Em cotação',
  comprado: 'Comprado',
  recebido: 'Recebido',
  entregue: 'Entregue',
};
const SITUACAO_ORDEM: ItemSituacao[] = ['a_cotar', 'em_cotacao', 'comprado', 'recebido', 'entregue'];
/** FX-7: ponto colorido da etapa de cada item.
 *  FASE-C: `bg-indigo-500` (cor Tailwind crua, fora da paleta warm — última
 *  sobrevivente apontada no relatório da FASE-B) aposentada. A série usa só
 *  tokens da casa: cinza → âmbar → petróleo → verde meio-tom → verde cheio.
 *  O ponto é decorativo (o rótulo/`title` ao lado carrega a informação). */
const SITUACAO_DOT: Record<ItemSituacao, string> = {
  a_cotar: 'bg-ink-3 dark:bg-ink-dark-3',
  em_cotacao: 'bg-warning',
  comprado: 'bg-info',
  recebido: 'bg-success/50 dark:bg-success/60',
  entregue: 'bg-success',
};
/** FX-7: urgência com cor (faixa de metadados). */
const URGENCIA_META: Record<SolicitacaoCompraUrgencia, { label: string; dot: string; text: string }> = {
  alta: { label: 'Alta', dot: 'bg-red', text: 'text-red' },
  media: { label: 'Média', dot: 'bg-warning', text: 'text-warning dark:text-[#D9AE6B]' },
  baixa: { label: 'Baixa', dot: 'bg-ink-3 dark:bg-ink-dark-3', text: 'text-ink-2 dark:text-ink-dark-2' },
};

/** FX-14: prazo vencido = data (YYYY-MM-DD) anterior a hoje. Texto livre legado → false. */
export function isPrazoVencido(prazo: string | null): boolean {
  if (!prazo || !/^\d{4}-\d{2}-\d{2}$/.test(prazo)) return false;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const [y, m, d] = prazo.split('-').map(Number);
  return new Date(y, m - 1, d).getTime() < hoje.getTime();
}

/** Quantos itens já estão (pelo menos) comprados. */
function progressoComprados(card: QuadroCompraCard): { comprados: number; total: number } {
  const idxComprado = SITUACAO_ORDEM.indexOf('comprado');
  const comprados = card.itens.filter((i) => SITUACAO_ORDEM.indexOf(i.situacao) >= idxComprado).length;
  return { comprados, total: card.itens.length };
}

/** FX-6/FX-7: ações de cotação disponíveis aos cards/diálogos do quadro. */
interface QuadroCtxValue {
  /** Abre o chooser "Cotar" (criar/adicionar a cotação) para a solicitação. */
  openCotar: (card: QuadroCompraCard) => void;
  /** Navega pra cotação já ligada aos itens. */
  verCotacao: (cotacaoId: string) => void;
  /**
   * Fase 2: o gesto da coluna "A classificar" — dizer de quem é o gasto. Herdou o trabalho da
   * antiga aba "Fila do Compras", que morreu na fusão (decisão do Matheus, 15/09/2026).
   */
  openClassificar: (card: QuadroCompraCard) => void;
}
export const QuadroCtx = createContext<QuadroCtxValue | null>(null);
function useQuadroCtx(): QuadroCtxValue {
  const ctx = useContext(QuadroCtx);
  if (!ctx) throw new Error('QuadroCtx ausente');
  return ctx;
}

export function ComprasQuadroPage() {
  const comprasOn = useMyFeatureFlag('COMPRAS_ENABLED');
  const { data, isLoading, isError, refetch } = useQuadroCompras();
  const { changeStage } = useDealActions('compras');
  const { invalidarQuadro } = useQuadroActions();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  // FU-5: o painel de automações é o MESMO do Comercial (reusa o motor — o card é
  // um deal, então mover dispara as automações). Nenhuma ligada por padrão.
  const { columns, getLabel } = useKanbanColumns('compras', COMPRAS_STAGES);
  const [autoOpen, setAutoOpen] = useState(false);
  // FX-6: ao mover/abrir um card que ainda não tem cotação, abrir o chooser. O
  // `revertToSlug` (≠ null só no drag) desfaz o movimento se o usuário cancelar.
  const [cotar, setCotar] = useState<{ card: QuadroCompraCard; revertToSlug: string | null } | null>(null);
  // Fase 2: o card de "A classificar" abre o diálogo de vínculo aqui mesmo — sem segunda tela.
  const [classificar, setClassificar] = useState<QuadroCompraCard | null>(null);

  const emCotacaoSlugs = useMemo(
    () => new Set(columns.filter((c) => c.situacaoMapeada === 'em_cotacao').map((c) => c.slug)),
    [columns],
  );
  // FX-29: colunas de aprovação (read-only/derivadas).
  const APROVACAO_SLUGS = useMemo(() => new Set<string>(COMPRAS_APROVACAO_SLUGS), []);

  const verCotacao = (cotacaoId: string) => navigate(`/menu/compras/cotacoes?cotacao=${cotacaoId}`);
  const ctxValue: QuadroCtxValue = {
    openCotar: (card) => setCotar({ card, revertToSlug: null }),
    verCotacao,
    openClassificar: (card) => setClassificar(card),
  };

  if (!comprasOn) {
    return <p className="p-6 text-sm text-ink-3 dark:text-ink-dark-3">Compras indisponível.</p>;
  }

  const cards = data?.items ?? [];

  return (
    <QuadroCtx.Provider value={ctxValue}>
      {/* LAYOUT-CANONICO: o wrapper próprio (`px-4 md:px-8 py-6 w-full` +
          `flex flex-col h-[calc(100vh-3rem)] min-h-0`) virou a régua única —
          `full` porque o quadro rola na horizontal e `alturaCheia` porque é o
          mesmo FX-32 de sempre: a barra de rolagem horizontal fica fixa no
          rodapé em vez de flutuar no meio da tela. */}
      <PageContainer variante="full" alturaCheia>
        {/* FASE-C: PageHeader canônico (crumb + h1 + subtítulo); a legenda de
            cores vive no slot de ações — quebra linha limpa no 375px. */}
        <PageHeader
          crumb="Compras"
          title="Quadro"
          subtitle="Cada card é uma solicitação; os itens andam pelas colunas conforme a situação. Arraste o card para mover todos os itens, ou abra o card para ajustar item por item."
          actions={
            /* FX-18: legenda das cores (discreta, no canto). */
            <div className="flex flex-wrap items-center gap-3 text-xs text-ink-3 dark:text-ink-dark-3 pt-1">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded border border-info/40 dark:border-info/50 bg-info-tint/60 dark:bg-[rgba(35,78,107,0.35)]" />
                Externo (obra, projeto, venda ou cliente)
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface" />
                Interno
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded ring-1 ring-red/60" />
                Urgente
              </span>
            </div>
          }
        />

        {isLoading ? (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
        ) : isError ? (
          <div className="text-sm text-ink-2 dark:text-ink-dark-2">
            Não foi possível carregar o quadro.{' '}
            <button type="button" className="text-red underline" onClick={() => refetch()}>Tentar de novo</button>
          </div>
        ) : (
          <KanbanBoard<QuadroCompraCard>
            workflowSlug="compras"
            defaultStages={COMPRAS_STAGES}
            items={cards}
            getItemStage={(c) => c.fase}
            cardComponent={QuadroCard}
            isAdmin={isAdmin}
            onStageChange={async ({ item, fromSlug, toSlug, fromLabel, toLabel }) => {
              // 🔴 #347: a solicitação CANCELADA fica no Quadro, marcada, e não muda de coluna (o servidor recusa também).
              if (item.cancelada) {
                toast.show(`A solicitação #${item.numeroHumano ?? ''} foi cancelada: o card fica aqui, marcado, e não muda de coluna.`, { variant: 'error' });
                await invalidarQuadro();
                return;
              }
              // FX-29: colunas de aprovação são AUTOMÁTICAS (derivadas do status) —
              // não aceitam drag (nem entrar nem sair). Reverte + avisa.
              if ((APROVACAO_SLUGS as Set<string>).has(toSlug) || (APROVACAO_SLUGS as Set<string>).has(fromSlug)) {
                toast.show(
                  toSlug === 'a_classificar' || fromSlug === 'a_classificar'
                    ? 'Esta coluna é automática: o card sai daqui quando você classifica a solicitação (botão Classificar no card).'
                    : 'Esta coluna é automática — o card entra e sai pelas ações da cotação.',
                  { variant: 'error' },
                );
                await invalidarQuadro();
                return;
              }
              try {
                await changeStage(item.cardDealId, toSlug, '', '', { from: fromLabel, to: toLabel });
                await invalidarQuadro();
                // FX-6: entrou em "Em cotação" e ainda não tem cotação ligada →
                // abre o chooser. Cancelar desfaz o movimento (anti-acidente).
                if (emCotacaoSlugs.has(toSlug) && item.solicitacaoId && !item.cotacaoId) {
                  setCotar({ card: item, revertToSlug: fromSlug });
                }
              } catch (e) {
                toast.show(errMsg(e) ?? 'Não deu pra mover.', { variant: 'error' });
              }
            }}
            headerActions={
              isAdmin ? (
                <Button variant="ghost" size="sm" onClick={() => setAutoOpen(true)}>
                  <Settings2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
                  Automações
                </Button>
              ) : null
            }
            afterContent={
              isAdmin ? (
                <AutomationsPanel
                  open={autoOpen}
                  onClose={() => setAutoOpen(false)}
                  workflowSlug="compras"
                  columns={columns}
                  getColumnLabel={getLabel}
                  deals={[]}
                />
              ) : null
            }
            emptyState={{
              title: 'Sem solicitações ainda',
              description: 'Os cards chegam quando um projeto aprova material para Compras.',
            }}
            fillHeight
          />
        )}
      </PageContainer>

      {classificar && (
        <ClassificarSolicitacaoDialog
          solicitacaoId={classificar.solicitacaoId}
          onClose={() => setClassificar(null)}
          onClassificada={() => void invalidarQuadro()}
        />
      )}

      {cotar && (
        <CotarDialog
          card={cotar.card}
          onCancel={async () => {
            // Desfaz o movimento do drag (volta o card pra coluna de origem).
            if (cotar.revertToSlug) {
              try {
                await changeStage(cotar.card.cardDealId, cotar.revertToSlug, '', '');
                await invalidarQuadro();
              } catch {
                /* best-effort — o card fica na coluna nova, sem cotação */
              }
            }
            setCotar(null);
          }}
          onDone={(cotacaoId) => {
            setCotar(null);
            verCotacao(cotacaoId);
          }}
        />
      )}
    </QuadroCtx.Provider>
  );
}

/** FX-6: chooser de cotação ao entrar em "Em cotação" (ou via botão "Cotar"). */
function CotarDialog({
  card,
  onCancel,
  onDone,
}: {
  card: QuadroCompraCard;
  onCancel: () => void;
  onDone: (cotacaoId: string) => void;
}) {
  const toast = useToast();
  const criar = useCotacaoFromSolicitacao();
  const adicionar = useAddSolicitacaoToCotacao();
  const { data: cotacoesData } = useCotacoes();
  const abertas = (cotacoesData?.items ?? []).filter((c) => c.status === 'aberta');
  const [alvoCotacaoId, setAlvoCotacaoId] = useState('');
  const busy = criar.isPending || adicionar.isPending;

  const criarNova = () =>
    criar.mutate(card.solicitacaoId, {
      onSuccess: ({ cotacaoId }) => onDone(cotacaoId),
      onError: (e) => toast.show(errMsg(e) ?? 'Não deu pra criar a cotação.', { variant: 'error' }),
    });
  const adicionarExistente = () => {
    if (!alvoCotacaoId) return;
    adicionar.mutate(
      { cotacaoId: alvoCotacaoId, solicitacaoId: card.solicitacaoId },
      {
        onSuccess: ({ cotacaoId }) => onDone(cotacaoId),
        onError: (e) => toast.show(errMsg(e) ?? 'Não deu pra adicionar à cotação.', { variant: 'error' }),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogHeader>
        <DialogTitle>Cotar “{card.titulo}”</DialogTitle>
        <DialogDescription>Como você quer cotar os itens desta solicitação?</DialogDescription>
      </DialogHeader>
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div className="rounded-lg border border-line dark:border-line-dark p-3">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">Criar cotação nova</p>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
              Abre uma cotação só com os itens desta solicitação.
            </p>
            <Button size="sm" onClick={criarNova} disabled={busy}>
              {criar.isPending ? 'Criando…' : 'Criar cotação'}
            </Button>
          </div>

          <div className="rounded-lg border border-line dark:border-line-dark p-3">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 mb-1">Adicionar a uma cotação existente</p>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
              Junta os itens a uma cotação aberta (compra em volume).
            </p>
            {abertas.length === 0 ? (
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">Nenhuma cotação aberta no momento.</p>
            ) : (
              <div className="flex items-center gap-2">
                <Select size="sm" value={alvoCotacaoId} onChange={(e) => setAlvoCotacaoId(e.target.value)} className="w-56">
                  <SelectOption value="">— escolha a cotação —</SelectOption>
                  {abertas.map((c) => (
                    <SelectOption key={c.id} value={c.id}>{c.titulo}</SelectOption>
                  ))}
                </Select>
                <Button size="sm" variant="secondary" onClick={adicionarExistente} disabled={busy || !alvoCotacaoId}>
                  {adicionar.isPending ? 'Adicionando…' : 'Adicionar'}
                </Button>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancelar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function QuadroCard({ item: card }: KanbanCardProps<QuadroCompraCard>) {
  // 🔴 #347: a cancelada NÃO se arrasta (marcar, não esconder: o Quadro é o único endereço do pedido).
  const cancelada = !!card.cancelada;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: card.id, disabled: cancelada });
  const { openCotar, verCotacao, openClassificar } = useQuadroCtx();
  const [aberto, setAberto] = useState(false);
  const navigate = useNavigate();
  // Fase 2: sem dono ainda — o card está em "A classificar" e o gesto que o move é este.
  const precisaClassificar = !cancelada && card.fase === 'a_classificar' && !!card.solicitacaoId;
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
    : undefined;
  const { comprados, total } = progressoComprados(card);
  const urgenciaRealce = card.urgencia === 'alta';
  // FX-6: atalho de cotação no card. Card legado (sem solicitação) não cota; cancelada não cota.
  const podeCotar = podeCotarOCard(card);
  // FX-14: prazo vencido pisca vermelho (reusa o tratamento de SLA do Comercial).
  // Não pisca quando tudo já foi entregue (nada mais a cobrar).
  const reducedMotion = usePrefersReducedMotion();
  const tudoEntregue = card.itens.length > 0 && card.itens.every((i) => i.situacao === 'entregue');
  const prazoVencido = isPrazoVencido(card.prazo) && !tudoEntregue && !cancelada;

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        // 🔴 a cancelada não recebe os atributos de arrastar: o `aria-disabled` deles fazia o leitor de tela (e o Playwright) tratar
        // como desabilitado TUDO o que está dentro do card — inclusive o botão "Itens", que continua valendo (medido em 19/09).
        {...(cancelada ? {} : listeners)}
        {...(cancelada ? {} : attributes)}
        data-testid={`kanban-card-${card.cardDealId}`}
        data-prazo-vencido={prazoVencido || undefined}
        data-cancelada={cancelada || undefined}
        className={cn(
          'rounded-lg border p-3 transition-shadow',
          cancelada ? 'cursor-default opacity-75' : 'cursor-grab active:cursor-grabbing',
          // Distinção visual: projeto = azul; interno = cinza.
          card.interno
            ? 'bg-paper-surface dark:bg-paper-dark-surface border-line dark:border-line-dark'
            : 'bg-info-tint/60 dark:bg-[rgba(35,78,107,0.35)] border-info/40 dark:border-info/50',
          urgenciaRealce && !prazoVencido && 'ring-1 ring-red/60',
          // FX-14: prazo vencido — pisca (ou anel fixo se reduced-motion).
          prazoVencido && !reducedMotion && 'animate-pulse-red',
          prazoVencido && reducedMotion && 'ring-2 ring-red',
          isDragging ? 'opacity-50 shadow-lg' : 'hover:shadow-sm',
        )}
      >
        <div className="flex items-center gap-1.5 mb-1">
          {card.interno ? (
            <Badge variant="neutral" size="sm">Interno</Badge>
          ) : (
            <Badge variant="info" size="sm">Obra</Badge>
          )}
          {urgenciaRealce && !cancelada && <Badge variant="danger" size="sm">Urgente</Badge>}
          {cancelada && (
            <Badge variant="neutral" size="sm" data-testid={`cancelada-${card.cardDealId}`}>
              Cancelada
            </Badge>
          )}
          {card.numeroHumano != null && (
            <span className="ml-auto text-xs text-ink-3 dark:text-ink-dark-3 tabular-nums">#{card.numeroHumano}</span>
          )}
        </div>
        <div className={cn('text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate', cancelada && 'line-through')}>{card.titulo}</div>
        {cancelada && (
          <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2 break-words line-clamp-3" data-testid={`cancelada-motivo-${card.cardDealId}`}>
            {card.cancelada?.motivo ? `Motivo: ${card.cancelada.motivo}` : 'Cancelada sem motivo registrado.'}
          </p>
        )}
        {!cancelada && (card.avisos ?? 0) > 0 && (
          <p className="mt-1 text-xs text-warning dark:text-[#D9AE6B]" data-testid={`avisos-${card.cardDealId}`}>
            {card.avisos === 1 ? '1 item mudou no levantamento' : `${card.avisos} itens mudaram no levantamento`}: veja em Itens.
          </p>
        )}
        {/* 🔴 #365 (decisão 3): o aviso que faltou em julho — a mesma obra tem outros pedidos. ❌ O Quadro continua por status. */}
        {card.mesmaObra && card.projetoId && (
          <p className="mt-1 text-xs text-info dark:text-[#9CC2DC]" data-testid={`mesma-obra-${card.cardDealId}`}>
            Esta obra tem {card.mesmaObra.outras === 1 ? 'outra solicitação' : `outras ${card.mesmaObra.outras} solicitações`}
            {card.mesmaObra.numeros.length > 0 ? ` (${card.mesmaObra.numeros.map((n) => `#${n}`).join(', ')})` : ''}.{' '}
            <button
              type="button"
              className="inline-flex min-h-[44px] items-center underline underline-offset-2"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/projetos/${card.projetoId}?tab=compras`);
              }}
              data-testid={`mesma-obra-link-${card.cardDealId}`}
            >
              Ver a obra inteira
            </button>
          </p>
        )}
        {!card.interno && card.clienteNome && (
          <div className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">{card.clienteNome}</div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-ink-2 dark:text-ink-dark-2 tabular-nums">
            {comprados}/{total} comprados
          </span>
          <div className="flex items-center gap-2">
            {/* Fase 2: o gesto que tira o card de "A classificar" — o trabalho que era da aba Fila. */}
            {precisaClassificar && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  openClassificar(card);
                }}
                className="text-xs text-red hover:text-red-deep font-medium"
                data-testid={`classificar-${card.cardDealId}`}
              >
                Classificar
              </button>
            )}
            {podeCotar && !precisaClassificar && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (card.cotacaoId) verCotacao(card.cotacaoId);
                  else openCotar(card);
                }}
                className="text-xs text-ink-2 dark:text-ink-dark-2 hover:text-ink-1 dark:hover:text-ink-dark-1 font-medium"
              >
                {card.cotacaoId ? 'Ver cotação' : 'Cotar'}
              </button>
            )}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setAberto(true);
              }}
              className="text-xs text-red hover:text-red-deep font-medium"
            >
              Itens
            </button>
          </div>
        </div>
      </div>

      {aberto && <QuadroCardDialog card={card} onClose={() => setAberto(false)} />}
    </>
  );
}

export function QuadroCardDialog({ card, onClose }: { card: QuadroCompraCard; onClose: () => void }) {
  const { setItemSituacao } = useQuadroActions();
  const { openCotar, verCotacao } = useQuadroCtx();
  const toast = useToast();
  const { comprados, total } = progressoComprados(card);
  const urg = URGENCIA_META[card.urgencia];
  const cancelada = !!card.cancelada;
  const podeCotar = podeCotarOCard(card);
  // FX-20: número sequencial humano (#N).
  const solicRef = card.numeroHumano != null ? String(card.numeroHumano) : null;

  const cotacaoAction = () => {
    onClose();
    if (card.cotacaoId) verCotacao(card.cotacaoId);
    else openCotar(card);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }} size="lg">
      <DialogHeader>
        {/* FX-7/FX-13: cabeçalho — badge Obra/Interno + "Solicitação #N" + título + cliente curto. */}
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <Badge variant={card.interno ? 'neutral' : 'info'} size="sm">{card.interno ? 'Interno' : 'Obra'}</Badge>
          {solicRef && (
            <span className="text-xs text-ink-3 dark:text-ink-dark-3 tabular-nums">Solicitação #{solicRef}</span>
          )}
        </div>
        <DialogTitle>{card.titulo}</DialogTitle>
        {/* FX-13: não repetir o cliente quando ele já É o título. */}
        {!card.interno && card.clienteNome && card.clienteNome !== card.titulo && (
          <DialogDescription>{card.clienteNome}</DialogDescription>
        )}
      </DialogHeader>
      <DialogContent>
        {/* FX-7: faixa de metadados — solicitante · urgência (cor) · prazo. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4 text-xs text-ink-2 dark:text-ink-dark-2">
          {card.solicitanteNome && (
            <span>Solicitante: <span className="text-ink-1 dark:text-ink-dark-1">{card.solicitanteNome}</span></span>
          )}
          <span className="inline-flex items-center gap-1">
            <span className={cn('inline-block w-1.5 h-1.5 rounded-full', urg.dot)} />
            <span className={urg.text}>Urgência {urg.label.toLowerCase()}</span>
          </span>
          {card.prazo && <span>Prazo: <span className="text-ink-1 dark:text-ink-dark-1">{card.prazo}</span></span>}
        </div>

        {/* 🔴 #347: a cancelada diz o motivo, quem e quando — e os itens não mudam mais de situação. */}
        {cancelada && (
          <div className="mb-4 rounded-lg border border-line dark:border-line-dark bg-paper-warm dark:bg-paper-dark-warm p-3 text-sm" data-testid="dialogo-cancelada">
            <p className="font-medium text-ink-1 dark:text-ink-dark-1">Solicitação cancelada{card.cancelada?.porNome ? ` por ${card.cancelada.porNome}` : ''}.</p>
            <p className="text-ink-2 dark:text-ink-dark-2 break-words">{card.cancelada?.motivo ? `Motivo: ${card.cancelada.motivo}` : 'Sem motivo registrado.'}</p>
            <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">Ela fica no Quadro para o histórico. Os itens não mudam mais de situação e o card não muda de coluna.</p>
          </div>
        )}

        {card.itens.length === 0 ? (
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">
            Sem itens rastreados (card legado do kanban antigo). Arraste o card pelas colunas pra acompanhar.
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">Itens</h3>
              <Badge variant={comprados === total ? 'success' : 'neutral'} size="sm">
                {comprados}/{total} comprados
              </Badge>
            </div>
            {/* FX-13: lista rolável (header/rodapé fixos) — solicitações com muitos itens. */}
            <div className="divide-y divide-line dark:divide-line-dark max-h-[50vh] overflow-y-auto pr-1">
              {card.itens.map((it) => (
                <div key={it.id} className="py-2 flex items-start justify-between gap-3">
                  {/* FX-26: flex-1 min-w-0 garante que o nome ocupe a linha e quebre limpo. */}
                  <div className="flex-1 min-w-0 flex items-start gap-2">
                    <span
                      className={cn('inline-block w-2 h-2 rounded-full shrink-0 mt-1.5', SITUACAO_DOT[it.situacao])}
                      title={SITUACAO_LABEL[it.situacao]}
                    />
                    <div className="min-w-0">
                      {/* FX-13: nome do item NÃO corta — quebra de linha. */}
                      <p className="text-sm text-ink-1 dark:text-ink-dark-1 break-words">{it.item}</p>
                      <p className="text-xs text-ink-3 dark:text-ink-dark-3">Qtd: {it.quantidade}</p>
                      {/* 🔴 18/09 (o buraco): o levantamento mudou, o item saiu, ou comprou-se a mais. Nada muda sozinho. */}
                      {it.aviso && (
                        <p className="text-xs text-warning dark:text-[#D9AE6B] break-words" data-testid={`aviso-item-${it.id}`}>
                          Levantamento: {it.aviso}.
                        </p>
                      )}
                    </div>
                  </div>
                  {/* FX-26: container fixo encurta o wrapper w-full do Select (chevron alinhado). */}
                  <div className="w-36 shrink-0">
                    <Select
                      size="sm"
                      value={it.situacao}
                      disabled={cancelada}
                      onChange={(e) =>
                        setItemSituacao.mutate(
                          { itemId: it.id, situacao: e.target.value as ItemSituacao },
                          { onError: (err) => toast.show(errMsg(err) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }) },
                        )
                      }
                    >
                      {SITUACAO_ORDEM.map((s) => (
                        <SelectOption key={s} value={s}>{SITUACAO_LABEL[s]}</SelectOption>
                      ))}
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* FX-7: rodapé — só "Ver cotação"/"Cotar" (sem "Abrir projeto"). Ajuda vira legenda discreta. */}
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-line dark:border-line-dark pt-3">
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            A coluna do card acompanha o item mais atrasado.
          </p>
          {podeCotar && (
            <Button size="sm" variant={card.cotacaoId ? 'secondary' : 'primary'} onClick={cotacaoAction}>
              {card.cotacaoId ? 'Ver cotação' : 'Cotar'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
